//================================================================
//  Page: ApprovalDetailsM
//================================================================

//  Purpose: Detailed Approval page - shows individual approval in detail (Mobile View)

//================================================================

//Libraries
import { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import BreadCrumbs from '../../../Components/Breadcrumbs/BreadCrumbs';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';
import ActivityFeed from '../../../Components/ActivityFeed/ActivityFeed';
import ManageApproval from '../../../Components/ManageApproval/ManageApproval';
import TabViewM from '../../../Components/TabViewM/TabViewM';
import FormRenderer from '../../../Components/FormRenderer/FormRenderer';
import Footer from '../../../Components/Footer/Footer.js';

//Functions
import DocumentListener from '../../../Library/DocumentListener';
import ConvertDate from '../../../Library/ConvertDate';

//Images
import Expand from '../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../Components/Images/Icon_Expand_Teal.svg';
import FileIcon from '../../../Components/Images/Icon_File_Grey.svg';

//CSS

export default function ApprovalDetailsM() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const params = useParams();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine status of the page > 'pending', 'onload', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Contains Firestore Document of the request
    const [requestDoc, setRequestDoc] = useState(undefined);

    // Used to toggle view of details
    const [viewDetails, setViewDetails] = useState(true);

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Get the approval document
    useEffect(() => {

        const requestid = params.id;

        function onLoadChange(document) {

            const approvalTypeList = ['Initial Approval', 'Commercial Second Round Approval', 'Final Approval', 'Finance Approval', 'COO Approval', 'LoA Approval', 'Other Approval', 'Quote Review', 'Budget Owner (LoA) Approval', 'Finance Manager Approval']
            
            if (!(approvalTypeList.includes(document?.tasktype))) return setPageStatus('forbidden');

            setRequestDoc(document);

            setPageStatus('onload');

        }

        function onError(error) {
            console.log(error);

            setPageStatus('error-fatal');
        }

        const unsubscribe = DocumentListener('requests', requestid, onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        }

    }, [params, getUser])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            header={
                <div className='flex flex-col py-[21px] px-[18px] bg-neutral-100 h-full w-full border-solid border-1 border-neutral-300' hidden={pageStatus !== 'onload'}>
                    <BreadCrumbs rootLinkName={'My Approvals'} rootLinkRoute={'/approvals'} childLinkName={requestDoc?.parentrequestid?.toUpperCase()}></BreadCrumbs>
                    <h4 className='flex items-center h-full cursor-default text-[22px] mb-0'>{requestDoc?.taskname}</h4>
                </div>}
            body={
                <div className='flex flex-col'>

                    {/* APPROVAL INFORMATION */}
                    <div className='p-[18px]'>

                        <div className='flex flex-col gap-6'>

                            {/* Name */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Name</label>
                                <p className='m-0'>{requestDoc?.taskname}</p>
                            </div>

                            {/* Description */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Short Description</label>
                                <p className='m-0'>{requestDoc?.taskdescription}</p>
                            </div>

                            {/* Comment - Only visible in Final Approval */}
                            <div className='flex flex-col gap-3' hidden={!['Final Approval', 'Finance Approval', 'COO Approval', 'LoA Approval'].includes(requestDoc?.tasktype)}>
                                <label className='font-semibold'>Comment</label>
                                <p className='m-0'>{requestDoc?.finalapprovalcomment}</p>
                            </div>

                            {/* Assigned To */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Assigned To</label>
                                <div className='flex flex-col gap-1'>
                                    <p className='m-0' hidden={requestDoc?.assignedto?.email === 'Unassigned'}>
                                        {requestDoc?.assignedto?.givenname} {requestDoc?.assignedto?.surname}
                                    </p>
                                    <p className={`m-0 text-[#424242] ${requestDoc?.assignedto?.email !== 'Unassigned' && 'text-sm'}`}>
                                        {requestDoc?.assignedto?.email}
                                    </p>
                                </div>
                            </div>

                            {/* Status */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Status</label>
                                <StatusLabel status={requestDoc?.status} tasktype={requestDoc?.tasktype}></StatusLabel>
                            </div>

                            {/* Created Date */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Created Date</label>
                                <p className='m-0'>{ConvertDate(requestDoc?.createddate)}</p>
                            </div>

                            {/* Uploaded Documents */}
                            
                            {/* - Supporting Documents (Demand Management Approvals) */}
                            {
                                (requestDoc?.supportingdocs?.length > 0 || requestDoc?.supportingdocs !== undefined) &&
                                <div className='flex flex-col gap-3' hidden={requestDoc?.supportingdocs?.length === 0 || requestDoc?.supportingdocs === undefined}>
                                    <label className='font-semibold'>Uploaded Documents</label>
                                    <div className='flex flex-row gap-2 items-center border-1 border-[#D8D8D8] px-3 py-2 rounded-md'>
                                        <img src={FileIcon} alt='file'></img>
                                        <a href={requestDoc?.supportingdocs[0]?.fileUrl} target='_blank' rel='noopener noreferrer'>{requestDoc?.supportingdocs[0]?.fileName}</a>
                                    </div>
                                </div>
                            }

                            {/* - Attached Files (Quote Reviews) */}
                            {
                                (requestDoc?.attachedfiles?.length > 0 || requestDoc?.attachedfiles !== undefined) &&
                                <div className='flex flex-col gap-3' hidden={requestDoc?.attachedfiles?.length === 0 || requestDoc?.attachedfiles === undefined}>
                                    <label className='font-semibold'>Uploaded Documents</label>
                                    <div className='flex flex-row gap-2 items-center border-1 border-[#D8D8D8] px-3 py-2 rounded-md'>
                                        <img src={FileIcon} alt='file'></img>
                                        <a href={requestDoc?.attachedfiles[0]?.fileUrl} target='_blank' rel='noopener noreferrer'>{requestDoc?.attachedfiles[0]?.fileName}</a>
                                    </div>
                                </div>
                            }

                        </div>

                    </div>

                    <hr className='text-neutral-500 p-0 m-0'></hr>

                    {/* VIEW MORE DETAILS */}
                    <div className='flex flex-row gap-3 px-[18px] py-2 items-center justify-between cursor-pointer' onClick={() => setViewDetails(!viewDetails)}>

                        {/* Title */}
                        <div className='flex flex-row items-center gap-2 py-2'>
                            <img className='cursor-pointer' src={viewDetails ? Expand : Collapse} alt={'>'}></img>
                            <h4 className='text-[16px] m-0'> {viewDetails ? 'View Less Details' : 'View More Details'} </h4>
                        </div>

                    </div>

                    {/* Tab View of Supporting Information */}
                    <div className='flex flex-row justify-between' hidden={!viewDetails}>
                        <FormRenderer task={requestDoc}></FormRenderer>
                    </div>

                    <hr className='m-0 text-neutral-500'></hr>

                    {/* TRACKING HISTORY */}
                    <TabViewM
                        defaultView={1}
                        tabs={[
                            {
                                id: 1,
                                title: 'Tracking History',
                                content:
                                    <div className='p-2'>
                                        <ActivityFeed
                                            task={requestDoc}
                                        ></ActivityFeed>
                                    </div>,
                                disabled: false,
                            }
                        ]}
                    ></TabViewM>

                    <div
                        hidden={requestDoc?.status !== 'In Progress'}
                    >
                        <Footer></Footer>
                    </div>

                    {/* MANAGE APPROVAL - Only shows on pending approvals */}
                    <ManageApproval requestDoc={requestDoc} ></ManageApproval>

                </ div>}
        ></PageComponent>
    )
}